export interface ScenarioDocument {
  id: string;
  name: string;
  description: string;
  instructions?: string; // LLM-only override of description; UI never shows
  url: string; // signed GCS URL (short-lived)
}

export interface Persona {
  id: string;
  name: string;
  role: string;
  avatar: string; // signed GCS URL
  description: string;
  instructions: string;
  accessibleDocumentIds: string[]; // Access Control List
  bio?: string;
  // ElevenLabs voice id. Reaches editors only — the server strips it for
  // players, who ask for speech by personaId instead.
  voiceId?: string | null;
}

export interface Scenario {
  id: string;
  status: "draft" | "published";
  title: string;
  description: string;
  category: string;
  logo?: string;
  contextPrompt: string;
  personas: Persona[];
  documents: ScenarioDocument[];
  rubricId?: string; // selected rubric for grading/coaching; undefined = none
  // Whether this scenario is promoted into the shared catalog, visible to
  // every organization. Writable only by an admin.
  isGlobal?: boolean;
  hero?: string; // signed GCS URL for the wide key art
  playerRole?: string; // Markdown, player-facing
  objective?: string; // Markdown, player-facing
  briefing?: string; // Markdown, player-facing
}

export interface Session {
  id: string;
  userId: string;
  scenarioId: string;
  sessionNumber: number;
  name: string | null;
  createdAt: string; // ISO date string
  lastModifiedAt: string; // ISO date string
}

export interface Message {
  id: string;
  role: "user" | "model";
  text: string;
  timestamp: number;
  personaId: string;
  isError?: boolean;
  revealedDocId?: string; // The ID of a document revealed in this message
  filePaths?: string[];
}

export interface RevealedDocument {
  documentId: string;
  revealedByPersonaId: string;
  revealedAt: number;
}

export interface ChatSession {
  personaId: string;
  messages: Message[];
}

export type ChatHistoryState = Record<string, Message[]>;

export enum SessionTool {
  SCENARIO = "scenario",
  PERSONA = "persona",
  SCRATCHPAD = "scratchpad",
  ANSWER = "answer",
  COACH = "coach",
}

export interface CoachTurn {
  userPrompt: string | null;
  response: string;
  createdAt: string;
}

export interface PerformanceLevelView {
  name: string;
  minGrade: number;
  maxGrade: number;
  description: string;
}

export interface ReportCardSection {
  criterionId: string;
  name: string;
  kind: "conversation" | "solution";
  weight: number;
  grade: number;
  feedback: string;
  bandName: string | null;
  performanceLevels: PerformanceLevelView[];
}

export interface ReportCard {
  id: string;
  sessionId: string;
  summary: string | null;
  rubricName: string;
  frameworkName: string;
  scenarioTitle: string;
  personaCount: number;
  personasContacted: number;
  createdAt: string;
  sections: ReportCardSection[];
}

export interface SolutionView {
  answer: string | null;
  artifact: string | null;
  hasReportCard: boolean;
  // Whether the rubric frozen on the session at creation is set. Reported by
  // the server, which is the only side that knows — the scenario's current
  // rubricId is stripped for members and isn't equivalent anyway.
  gradable: boolean;
}

export enum AppView {
  LIBRARY = 'library',
  CHAT = 'chat',
}
